import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Plug, Play, RefreshCw, Activity, CheckCircle2, AlertTriangle } from "lucide-react";
import StatusBadge from "@/components/app/StatusBadge";

const SYSTEM_META = {
  intune: { label: "Microsoft Intune", vendor: "Microsoft" },
  entra: { label: "Microsoft Entra ID", vendor: "Microsoft" },
  autopilot: { label: "Windows Autopilot", vendor: "Microsoft" },
  abm: { label: "Apple Business Manager", vendor: "Apple" },
  jamf: { label: "Jamf Pro", vendor: "Jamf" },
  kandji: { label: "Kandji", vendor: "Kandji" },
  google: { label: "Google Workspace", vendor: "Google" },
  scalefusion: { label: "Scalefusion", vendor: "Scalefusion" },
  workspaceone: { label: "VMware Workspace ONE", vendor: "VMware" },
  hrms: { label: "HRMS", vendor: "HR platforms" },
  erp: { label: "ERP", vendor: "ERP systems" },
  courier: { label: "Courier Aggregator", vendor: "Logistics" },
  csv: { label: "CSV / Excel", vendor: "File-based" },
  rest: { label: "REST API", vendor: "Custom" },
  webhook: { label: "Webhook", vendor: "Custom" },
};

export default function Connectors() {
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => { api.get("/connectors/health").then(({ data }) => setHealth(data)); };
  useEffect(load, []);

  const connect = async (c) => {
    setBusy(c.id);
    try {
      await api.patch(`/connectors/${c.id}`, { status: "connected", auth_status: "authorized" });
      toast.success(`${c.name} connected`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(null); }
  };
  const sync = async (c) => {
    setBusy(c.id);
    try {
      const { data } = await api.post(`/connectors/${c.id}/sync`);
      toast.success(`Sync complete: ${data.received} received, ${data.matched} matched`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-6 max-w-[1600px]">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Asset Intelligence</div>
        <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Connectors & Integration Health</h1>
        <p className="text-sm text-slate-500 mt-0.5">Connect external systems as data sources. Golden records are never overwritten directly.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <HealthTile label="Total" value={health?.total ?? "…"} icon={Plug} />
        <HealthTile label="Connected" value={health?.connected ?? "…"} icon={CheckCircle2} tone="green" />
        <HealthTile label="Disconnected" value={health?.disconnected ?? "…"} icon={AlertTriangle} tone="amber" />
        <HealthTile label="Stale" value={health?.stale ?? "…"} icon={Activity} tone="red" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {(health?.connectors || []).map((c) => {
          const meta = SYSTEM_META[c.system] || { label: c.system, vendor: "" };
          return (
            <div key={c.id} className="bg-white border border-slate-200 rounded-md p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">{meta.vendor}</div>
                  <div className="font-heading font-semibold text-slate-900 mt-0.5">{meta.label}</div>
                  <div className="text-xs text-slate-500">{c.name}</div>
                </div>
                <StatusBadge value={c.status} />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-1 text-xs">
                <Stat k="Auth" v={c.auth_status} />
                <Stat k="Health" v={c.api_health} />
                <Stat k="Received" v={c.records_received?.toLocaleString?.() || 0} />
                <Stat k="Matched" v={c.records_matched?.toLocaleString?.() || 0} />
              </div>
              <div className="mt-3 flex gap-2">
                {c.status !== "connected" ? (
                  <Button size="sm" variant="outline" onClick={() => connect(c)} disabled={busy === c.id}>
                    <Plug className="h-4 w-4 mr-1.5" /> Connect
                  </Button>
                ) : (
                  <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" onClick={() => sync(c)} disabled={busy === c.id}>
                    {busy === c.id ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <Play className="h-4 w-4 mr-1.5" />}
                    Sync now
                  </Button>
                )}
              </div>
              {c.last_success_sync && <div className="mt-2 text-[10px] text-slate-400">Last sync {new Date(c.last_success_sync).toLocaleString()}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ k, v }) {
  return <div><span className="text-slate-400">{k}: </span><span className="text-slate-800">{v ?? "—"}</span></div>;
}
function HealthTile({ label, value, icon: Icon, tone }) {
  const toneCls = { green: "text-emerald-700", amber: "text-amber-700", red: "text-rose-700" }[tone] || "text-slate-900";
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4">
      <div className="flex items-start justify-between">
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">{label}</div>
        <Icon className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
      </div>
      <div className={`mt-2 text-3xl font-heading font-bold tabular-nums ${toneCls}`}>{value}</div>
    </div>
  );
}
